import { useMemo, useRef, useState } from "react";
import { REALMS, t } from "@chuzi/shared/config";
import type { RealmId, UserProfile } from "@chuzi/shared/types";
import { applyRuntimeRealmLexiconOverride } from "../lib/realm";

type HierarchyField = {
  key: string;
  helperKey?: string;
  helperFallback?: string;
};

type HierarchyGroup = {
  id: string;
  titleKey: string;
  titleFallback: string;
  fields: HierarchyField[];
};

type RealmMappingState = Record<RealmId, Record<string, string>>;

const REALM_IDS: RealmId[] = ["cosmos", "wilds"];

const HIERARCHY_GROUPS: HierarchyGroup[] = [
  {
    id: "major_idea",
    titleKey: "experience_mapping_group_major_idea",
    titleFallback: "Major Idea",
    fields: [
      {
        key: "experience_realm_idea",
        helperKey: "experience_mapping_helper_realm_idea",
        helperFallback: "Highest-level concept for this realm.",
      },
    ],
  },
  {
    id: "experience_root",
    titleKey: "experience_mapping_group_experience_root",
    titleFallback: "Experience Root",
    fields: [
      {
        key: "story",
        helperKey: "experience_mapping_helper_story",
        helperFallback: "Largest narrative unit.",
      },
      {
        key: "scene",
        helperKey: "experience_mapping_helper_scene",
        helperFallback: "Contained unit under story.",
      },
      {
        key: "scene_choice",
        helperKey: "experience_mapping_helper_scene_choice",
        helperFallback: "Branching choice in a scene.",
      },
      {
        key: "node",
        helperKey: "experience_mapping_helper_node",
        helperFallback: "Smallest flow node.",
      },
    ],
  },
  {
    id: "node_types",
    titleKey: "experience_mapping_group_node_types",
    titleFallback: "Node Types",
    fields: [
      { key: "node_type_choice" },
      { key: "node_type_media" },
      { key: "node_type_go_to_scene" },
    ],
  },
  {
    id: "scene_structure",
    titleKey: "experience_mapping_group_scene_structure",
    titleFallback: "Scene Structure",
    fields: [
      { key: "editor_title_scene" },
      { key: "editor_ending_scene" },
      { key: "scene_graph" },
    ],
  },
  {
    id: "media_terms",
    titleKey: "experience_mapping_group_media",
    titleFallback: "Media Terms",
    fields: [
      { key: "editor_media_upload" },
      { key: "editor_media_preview" },
      { key: "editor_media_question" },
      { key: "editor_media_type_text" },
      { key: "editor_media_type_imagery" },
      { key: "editor_media_type_film" },
      { key: "media_pool" },
    ],
  },
  {
    id: "counts_adjacent",
    titleKey: "experience_mapping_group_counts",
    titleFallback: "Counts and Adjacent Labels",
    fields: [
      { key: "scenes_count" },
      { key: "choices_count" },
      { key: "story_picker_label" },
    ],
  },
];

const FLAT_KEYS: string[] = HIERARCHY_GROUPS.flatMap((group) =>
  group.fields.map((field) => field.key),
);

const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: 0.8,
  color: "rgba(232,240,255,0.58)",
  marginBottom: 8,
};

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  background: "rgba(126,184,255,0.06)",
  border: "1px solid rgba(126,184,255,0.35)",
  borderRadius: 8,
  color: "#e8f0ff",
  padding: "8px 10px",
  fontSize: 12,
  outline: "none",
  boxSizing: "border-box",
};

const BTN_STYLE: React.CSSProperties = {
  background: "rgba(126,184,255,0.12)",
  border: "1px solid rgba(126,184,255,0.35)",
  color: "#e8f0ff",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};

function buildInitialMapping(): RealmMappingState {
  return {
    cosmos: buildRealmMapping("cosmos"),
    wilds: buildRealmMapping("wilds"),
  };
}

function buildRealmMapping(realmId: RealmId): Record<string, string> {
  const realmLexicon = REALMS[realmId].lexicon;
  const next: Record<string, string> = {};
  for (const key of FLAT_KEYS) {
    next[key] = realmLexicon[key] ?? t(realmId, key, key);
  }
  return next;
}

export function DirectorExperienceMapping({
  user,
}: {
  user: UserProfile;
}) {
  const realm = user.realm ?? "cosmos";
  const isAdmin = !!user.is_admin;

  const initialRef = useRef<RealmMappingState>(buildInitialMapping());
  const [mapping, setMapping] = useState<RealmMappingState>(initialRef.current);
  const [selectedRealm, setSelectedRealm] = useState<RealmId>(
    user.realm ?? "cosmos",
  );
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const [saveState, setSaveState] = useState<"idle" | "saved">("idle");

  const dirtyCounts = useMemo(() => {
    const next: Record<RealmId, number> = { cosmos: 0, wilds: 0 };
    for (const realmId of REALM_IDS) {
      for (const key of FLAT_KEYS) {
        if (mapping[realmId][key] !== initialRef.current[realmId][key]) {
          next[realmId] += 1;
        }
      }
    }
    return next;
  }, [mapping]);

  if (!isAdmin) {
    return (
      <div style={{ fontSize: 13, color: "rgba(255,180,180,0.95)" }}>
        {t(
          realm,
          "experience_mapping_forbidden",
          "You do not have admin access for this section.",
        )}
      </div>
    );
  }

  function updateValue(realmId: RealmId, key: string, value: string) {
    setCopyState("idle");
    setSaveState("idle");
    setMapping((prev) => ({
      ...prev,
      [realmId]: {
        ...prev[realmId],
        [key]: value,
      },
    }));
  }

  async function handleCopy() {
    const fullMapping = mapping[selectedRealm];
    const payload = { [selectedRealm]: fullMapping };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyState("copied");
    } catch {
      setCopyState("idle");
    }
  }

  function handleSaveToExperience() {
    applyRuntimeRealmLexiconOverride(selectedRealm, mapping[selectedRealm]);
    setSaveState("saved");
  }

  function getSelectedDirtyCount() {
    let changed = 0;
    for (const key of FLAT_KEYS) {
      const current = mapping[selectedRealm][key];
      const initial = initialRef.current[selectedRealm][key];
      if (current !== initial) {
        changed += 1;
      }
    }
    return changed;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minHeight: 340 }}>
      <div style={{ fontSize: 12, color: "rgba(232,240,255,0.72)" }}>
        {t(
          realm,
          "experience_mapping_intro",
          "Edit realm vocabulary from largest to smallest. Select one realm and copy its JSON updates.",
        )}
      </div>

      <div style={{ fontSize: 12, color: "rgba(232,240,255,0.72)" }}>
        {getSelectedDirtyCount() > 0
          ? `${getSelectedDirtyCount()} ${t(
              realm,
              "experience_mapping_dirty",
              "dirty changes",
            )}`
          : t(
              realm,
              "experience_mapping_export_ready",
              "No unsaved mapping edits yet.",
            )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "170px 1fr",
          gap: 8,
          alignItems: "center",
        }}
      >
        <label style={{ fontSize: 12, color: "rgba(232,240,255,0.7)" }}>
          {t(realm, "experience_mapping_select_realm", "Select realm")}
        </label>
        <select
          value={selectedRealm}
          onChange={(e) => {
            setSelectedRealm(e.target.value as RealmId);
            setCopyState("idle");
          }}
          style={INPUT_STYLE}
        >
          {REALM_IDS.map((realmId) => (
            <option key={realmId} value={realmId}>
              {REALMS[realmId].label}
            </option>
          ))}
        </select>
      </div>

      <section
        style={{
          border: "1px solid rgba(126,184,255,0.35)",
          borderRadius: 12,
          background: "rgba(126,184,255,0.05)",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minWidth: 0,
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {REALMS[selectedRealm].label}
          </div>
          <div style={{ fontSize: 12, color: "rgba(232,240,255,0.72)" }}>
            {dirtyCounts[selectedRealm]}{" "}
            {t(realm, "experience_mapping_dirty", "dirty changes")}
          </div>
        </header>

        {HIERARCHY_GROUPS.map((group) => (
          <div key={group.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={SECTION_TITLE_STYLE}>
              {t(realm, group.titleKey, group.titleFallback)}
            </div>
            {group.fields.map((field) => {
              const value = mapping[selectedRealm][field.key] ?? "";
              return (
                <div
                  key={`${selectedRealm}-${field.key}`}
                  style={{
                    border: "1px solid rgba(126,184,255,0.2)",
                    borderRadius: 8,
                    padding: 8,
                    background: "rgba(8,14,28,0.5)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "150px 1fr",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(232,240,255,0.62)",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        wordBreak: "break-word",
                      }}
                    >
                      {field.key}
                    </div>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) =>
                        updateValue(selectedRealm, field.key, e.target.value)
                      }
                      style={INPUT_STYLE}
                    />
                  </div>
                  {field.helperKey ? (
                    <div style={{ fontSize: 11, color: "rgba(232,240,255,0.56)" }}>
                      {t(
                        realm,
                        field.helperKey,
                        field.helperFallback ?? "",
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </section>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          style={BTN_STYLE}
          onClick={handleSaveToExperience}
        >
          {t(
            realm,
            "experience_mapping_save_experience",
            "Save to experience",
          )}
        </button>
        <button
          type="button"
          style={BTN_STYLE}
          onClick={() => void handleCopy()}
        >
          {t(realm, "experience_mapping_copy_json", "Copy JSON")}
        </button>
      </div>

      <div style={{ fontSize: 12, color: "rgba(232,240,255,0.72)" }}>
        {saveState === "saved"
          ? t(
              realm,
              "experience_mapping_saved_experience",
              "Saved to current experience. Some labels appear immediately.",
            )
          : copyState === "copied"
          ? t(realm, "experience_mapping_copied", "Copied")
          : t(
              realm,
              "experience_mapping_paste_instruction",
              "Copy JSON for the selected realm and paste it in chat for code updates.",
            )}
      </div>
    </div>
  );
}
